import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ChevronDown, Check, ShoppingCart, Sparkles } from 'lucide-react';
import type { DtpProductResponse } from '@bloom/shared';
import { familyProgramDosing, familyDosingSections, planDisplayName, planKey, type DosingSection } from '../../lib/programDosing';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { EducationalShell } from '../../components/dtp/educational/EducationalShell';
import { useEducationalCatalog, getFamilyDisplayName } from '../../components/dtp/educational/useEducationalCatalog';
import { doseFamilyKey, useDtpCartStore } from '../../store/useDtpCartStore';
import { toast } from '../../lib/toast';
import { trackDtpEvent } from '../../lib/dtpAnalytics';
import { HeroIllustration } from '../../components/dtp/educational/HeroIllustration';

const HERO_TINTS: Record<string, string> = {
  'weight-loss': 'from-[#e8f0e9] via-[#a8c4ac] to-[var(--store-accent,#2d4a3a)]',
  'oral-ed': 'from-[#ede3d8] via-[#b89876] to-[#6b4a26]',
  'sexual-health': 'from-[#fbe8e8] via-[#d4a0a0] to-[#8b3c3c]',
  'dermatology': 'from-[#f5ebd8] via-[#d4a574] to-[#8b6332]',
  'peptides-healing': 'from-[#fbe8e8] via-[#d4a0a0] to-[#8b3c3c]',
  'peptides-growth-hormone': 'from-[#e3eef5] via-[#8fb3c8] to-[#3a5a78]',
  'peptides-metabolism': 'from-[#f5ebd8] via-[#d4a574] to-[#8b6332]',
  'peptides-collagen': 'from-[#e8f0e9] via-[#a8c4ac] to-[var(--store-accent,#2d4a3a)]',
  'daily-vitamins': 'from-[#e3eef5] via-[#8fb3c8] to-[#3a5a78]',
};

export function DtpLearnFamily() {
  const { categorySlug, familySlug } = useDtpParams<{ categorySlug: string; familySlug: string }>();
  const navigate = useNavigate();
  const { loading, error, clinic, products, categoryContent, physicianId, intakePrograms } = useEducationalCatalog();

  const familyProducts = useMemo(
    () => products.filter(p => p.drugFamilySlug === familySlug),
    [products, familySlug]
  );
  const sample = familyProducts[0];
  const educational = sample?.educationalContent;
  const recommender = sample?.dosageRecommender;
  const familyDisplayName = familySlug ? getFamilyDisplayName(familySlug, products) : '';

  const category = useMemo(
    () => categoryContent.find(c => c.slug === categorySlug),
    [categoryContent, categorySlug]
  );
  const parentCategory = category?.parentSlug
    ? categoryContent.find(c => c.slug === category.parentSlug)
    : null;

  // The clinic's intake-program dosage plans for this family (e.g. sema/tirz
  // starter vs. continuation). When present they are the source of truth for
  // doses shown here, so the educational page matches what intake prescribes.
  const programPlans = useMemo(
    () => familyProgramDosing(intakePrograms, familyProducts),
    [intakePrograms, familyProducts]
  );
  // Patient's state, when we know it: their saved address, or the only state a
  // single-state clinic serves. Plans restricted elsewhere are marked unavailable.
  const patientAddressState = useDtpAuthStore(s => s.patient?.address?.state);
  const patientState = patientAddressState || (clinic?.allowedStates?.length === 1 ? clinic.allowedStates[0] : undefined);
  const dosingSections = useMemo(
    () => familyDosingSections(programPlans, { clinicStates: clinic?.allowedStates, patientState }),
    [programPlans, clinic?.allowedStates, patientState]
  );
  const dosingOptions = dosingSections.flatMap(section => section.options);
  const [selectedPlanKey, setSelectedPlanKey] = useState<string>();
  const selectedPlan = (dosingOptions.find(o => o.key === selectedPlanKey && o.available) || dosingOptions.find(o => o.available))?.plan;
  // Guided intake for the selected plan's program, else any guided program in this family.
  const guidedPrograms = clinic?.entryFlow && clinic.entryFlow.mode !== 'direct' && !clinic.isVet
    ? clinic.entryFlow.programIds
    : [];
  const guidedProgramId = selectedPlan && guidedPrograms.includes(selectedPlan.programId)
    ? selectedPlan.programId
    : programPlans.find(p => guidedPrograms.includes(p.programId))?.programId;

  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Without program plans, every SKU is a visible dose option; this is the pick.
  const [selectedSkuId, setSelectedSkuId] = useState<string>();

  const trackAdd = (productId: string, source: string) => {
    if (!clinic) return;
    trackDtpEvent({
      physicianId: clinic.physicianId,
      variant: clinic.storefrontVariant,
      eventType: 'add_to_cart',
      categorySlug: categorySlug || undefined,
      familySlug: familySlug || undefined,
      productId,
      source,
    });
  };

  // Track family_view once per slug
  const lastFamilyTracked = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && clinic && familySlug && lastFamilyTracked.current !== familySlug) {
      lastFamilyTracked.current = familySlug;
      trackDtpEvent({
        physicianId: clinic.physicianId,
        variant: clinic.storefrontVariant,
        eventType: 'family_view',
        categorySlug: categorySlug || undefined,
        familySlug,
      });
    }
  }, [loading, clinic, familySlug, categorySlug]);

  // Track dose_finder_complete when all questions are answered
  const doseFinderTracked = useRef<string | null>(null);
  useEffect(() => {
    if (!recommender?.enabled || !clinic || !familySlug) return;
    const allAnswered = recommender.questions.every(q => answers[q.id]);
    const key = `${familySlug}:${recommender.questions.map(q => answers[q.id] || '').join('|')}`;
    if (allAnswered && doseFinderTracked.current !== key) {
      doseFinderTracked.current = key;
      trackDtpEvent({
        physicianId: clinic.physicianId,
        variant: clinic.storefrontVariant,
        eventType: 'dose_finder_complete',
        categorySlug: categorySlug || undefined,
        familySlug,
      });
    }
  }, [answers, recommender, clinic, categorySlug, familySlug]);

  const recommendation = useMemo(() => {
    if (dosingOptions.length > 0) {
      // Program plans are authoritative: no available plan means nothing to offer here.
      const product = selectedPlan && familyProducts.find(p => p._id === selectedPlan.productId);
      return product ? { product, rationale: `${planDisplayName(selectedPlan)} · ${selectedPlan.doseLabel}` } : null;
    }
    if (recommender?.enabled && recommender.rules.length > 0) {
      for (const rule of recommender.rules) {
        const matches = Object.entries(rule.when).every(([qid, expected]) => {
          const ans = answers[qid];
          if (typeof expected === 'string') return ans === expected;
          // numeric range
          if (expected && typeof expected === 'object') {
            const num = Number(ans);
            if (Number.isNaN(num)) return false;
            const range = expected as { gte?: number; lte?: number };
            if (range.gte !== undefined && num < range.gte) return false;
            if (range.lte !== undefined && num > range.lte) return false;
            return true;
          }
          return false;
        });
        if (matches) {
          const product = familyProducts.find(p => p._id === rule.recommendProductId);
          if (product && !selectedSkuId) return { product, rationale: rule.rationale };
        }
      }
    }
    // The dose the patient picked, else the lowest strength.
    const picked = familyProducts.find(p => p._id === selectedSkuId) || sortByStrength(familyProducts)[0];
    return picked ? { product: picked, rationale: '' } : null;
  }, [answers, familyProducts, recommender, selectedPlan, dosingOptions.length, selectedSkuId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--store-bg,#faf8f4)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--store-accent,#2d4a3a)]" />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-[var(--store-bg,#faf8f4)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-serif text-2xl text-[var(--store-text,#1a1612)]">{error || 'Store unavailable'}</h1>
        </div>
      </div>
    );
  }

  if (familyProducts.length === 0) {
    return (
      <EducationalShell clinic={clinic}>
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-20 text-center">
          <h1 className="font-serif text-3xl">Medication not found</h1>
          <p className="text-[var(--store-muted,#8a8175)] mt-2">This product isn't available in this store.</p>
          <button onClick={() => navigate(dtpPath(physicianId, `/learn`))} className="mt-6 underline text-[var(--store-accent,#2d4a3a)]">Back to shop</button>
        </div>
      </EducationalShell>
    );
  }

  const heroTint = HERO_TINTS[categorySlug || ''] || 'from-[#e8f0e9] via-[#a8c4ac] to-[var(--store-accent,#2d4a3a)]';

  return (
    <EducationalShell clinic={clinic}>
      <div className="bg-[var(--store-bg,#faf8f4)] pb-24 md:pb-0">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-3 md:py-4 text-xs text-[var(--store-muted,#8a8175)] uppercase tracking-wider">
          <button onClick={() => navigate(dtpPath(physicianId, `/learn`))} className="hover:text-[var(--store-text,#1a1612)]">Shop</button>
          <span className="mx-2">/</span>
          {parentCategory && (
            <>
              <button onClick={() => navigate(dtpPath(physicianId, `/learn/${parentCategory.slug}`))} className="hover:text-[var(--store-text,#1a1612)]">{parentCategory.displayName}</button>
              <span className="mx-2">/</span>
            </>
          )}
          {category && (
            <>
              <button onClick={() => navigate(dtpPath(physicianId, `/learn/${category.slug}`))} className="hover:text-[var(--store-text,#1a1612)]">{category.displayName}</button>
              <span className="mx-2">/</span>
            </>
          )}
          <span className="text-[var(--store-text,#1a1612)]">{familyDisplayName}</span>
        </div>

        <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-14 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          {/* Left: editorial */}
          <div className="lg:col-span-7">
            {category && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium tracking-[0.06em] uppercase bg-[#e8f0e9] text-[var(--store-accent,#2d4a3a)]">
                {category.displayName}
              </span>
            )}
            <h1 className="font-serif text-[2.25rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4rem] font-medium leading-[1] tracking-tight mt-4">
              {familyDisplayName}
            </h1>
            {educational?.shortPitch && (
              <p className="mt-4 md:mt-5 text-lg md:text-xl text-[var(--store-muted,#4a4238)] leading-relaxed font-light">
                {educational.shortPitch}
              </p>
            )}

            <div className={`relative mt-8 md:mt-10 aspect-[16/9] rounded-2xl bg-gradient-to-br ${heroTint} shadow-[0_30px_60px_-15px_rgba(26,22,18,0.2)] overflow-hidden`}>
              <HeroIllustration slug={categorySlug} />
            </div>

            <div className="lg:hidden mt-8">
              <a href="#dose-finder" className="block w-full text-center border border-[var(--store-border,#e8e2d6)] rounded-full py-3 font-medium hover:border-[var(--store-text,#1a1612)]">Find your dose →</a>
            </div>

            <div className="mt-8 md:mt-10 space-y-6 md:space-y-8">
              {educational?.mechanism && <ProseBlock heading="How it works" body={educational.mechanism} />}
              {educational?.whoItsFor && <ProseBlock heading="Who it's for" body={educational.whoItsFor} />}
              {educational?.whatToExpect && <ProseBlock heading="What to expect" body={educational.whatToExpect} />}
              {educational?.contraindications && <ProseBlock heading="Who should not take this" body={educational.contraindications} />}

              {educational?.faq && educational.faq.length > 0 && (
                <div>
                  <h3 className="font-serif text-xl md:text-2xl font-medium tracking-tight mb-2">Common questions</h3>
                  {educational.faq.map((item, idx) => (
                    <details key={idx} className="border-t border-[var(--store-border,#e8e2d6)] py-5 last:border-b">
                      <summary className="cursor-pointer font-medium text-[16px] md:text-[17px] flex items-start justify-between gap-4 list-none">
                        <span>{item.q}</span>
                        <ChevronDown className="w-5 h-5 text-[var(--store-muted,#8a8175)] mt-0.5 flex-shrink-0 transition-transform [details[open]_&]:rotate-180" />
                      </summary>
                      <p className="mt-3 text-[15px] text-[var(--store-muted,#4a4238)] leading-[1.75]">{item.a}</p>
                    </details>
                  ))}
                </div>
              )}

              {!educational?.mechanism && !educational?.whoItsFor && !educational?.whatToExpect && (
                <div className="text-[var(--store-muted,#8a8175)] italic">More information coming soon. Talk to {clinic.name} during a consult.</div>
              )}
            </div>
          </div>

          {/* Right: dose finder */}
          <aside id="dose-finder" className="lg:col-span-5">
            {clinic.storefrontVariant === 'custom' && clinic.customDesign?.productPages?.some(page => familyProducts.some(p => p._id === page.productId)) && (
              <section className="mb-6 rounded-2xl border border-[var(--store-border)] bg-white p-6">
                <h2 className="font-serif text-xl font-medium">Explore treatment options</h2>
                <div className="mt-3 space-y-2">
                  {clinic.customDesign.productPages.filter(page => familyProducts.some(p => p._id === page.productId)).map(page => (
                    <button key={page.productId} className="block text-left text-sm underline text-[var(--store-accent)] py-2" onClick={() => navigate(dtpPath(physicianId, `/products/${page.slug}`))}>{page.heading}</button>
                  ))}
                </div>
              </section>
            )}
            <div className="lg:sticky lg:top-6">
              <div className="bg-white border border-[var(--store-border,#e8e2d6)] rounded-2xl p-6 md:p-8 shadow-[0_10px_40px_-10px_rgba(26,22,18,0.08)]">
                <div className="flex items-center gap-2 text-[11px] md:text-xs uppercase tracking-[0.2em] text-[var(--store-accent,#2d4a3a)] mb-3 font-medium">
                  <Sparkles className="w-4 h-4" />Dose finder
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-medium tracking-tight">Find your starting dose</h3>
                <p className="text-[13px] md:text-[14px] text-[var(--store-muted,#8a8175)] mt-2 leading-relaxed">
                  {dosingOptions.length > 0
                    ? `Doses ${clinic.name} prescribes through its treatment programs.`
                    : recommender?.enabled && recommender.questions.length > 0
                      ? `${recommender.questions.length} quick question${recommender.questions.length === 1 ? '' : 's'} to help you pick.`
                      : familyProducts.length > 1 ? `${familyProducts.length} strengths. Pick the one your clinician recommended.` : '1 option available.'}
                </p>

                {dosingOptions.length > 0 ? (
                  <ProgramDosingPlans
                    sections={dosingSections}
                    selectedKey={selectedPlan && planKey(selectedPlan)}
                    onSelect={setSelectedPlanKey}
                    guidedHref={guidedProgramId ? dtpPath(physicianId, `/intake/${guidedProgramId}`) : undefined}
                  />
                ) : recommender?.enabled && recommender.questions.length > 0 && (
                  <div className="space-y-5 mt-7">
                    {recommender.questions.map((q) => (
                      <div key={q.id}>
                        <label className="block text-[13px] font-medium mb-2">{q.prompt}</label>
                        {q.type === 'choice' && q.choices && (
                          <div className={`grid gap-2 ${q.choices.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                            {q.choices.map(c => (
                              <button
                                key={c.value}
                                onClick={() => setAnswers(a => ({ ...a, [q.id]: c.value }))}
                                className={`rounded-xl px-3 py-3 text-sm min-h-[48px] transition-colors ${
                                  answers[q.id] === c.value
                                    ? 'border-2 border-[var(--store-text,#1a1612)] bg-white font-medium'
                                    : 'border border-[var(--store-border,#e8e2d6)] bg-white text-[var(--store-muted,#8a8175)] hover:border-[var(--store-muted,#8a8175)]'
                                }`}
                              >{c.label}</button>
                            ))}
                          </div>
                        )}
                        {q.type === 'number' && (
                          <input
                            type="number"
                            value={answers[q.id] || ''}
                            onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                            className="w-full border border-[var(--store-border,#e8e2d6)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--store-text,#1a1612)]"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {dosingOptions.length === 0 && familyProducts.length > 1 && (
                  <SkuDoseOptions
                    products={familyProducts}
                    selectedId={recommendation?.product._id}
                    onSelect={setSelectedSkuId}
                  />
                )}

                {recommendation && (
                  <DoseRecommendation
                    product={recommendation.product}
                    rationale={recommendation.rationale}
                    onAddTracked={trackAdd}
                  />
                )}

              </div>
            </div>
          </aside>
        </div>

        {/* Mobile sticky buy bar */}
        {recommendation && (
          <MobileBuyBar product={recommendation.product} label={selectedPlan?.doseLabel || familyDisplayName} onAddTracked={trackAdd} />
        )}
      </div>

    </EducationalShell>
  );
}

const PATH_CHIPS = { starter: 'Starting', continuation: 'Continuing' } as const;

function ProgramDosingPlans({ sections, selectedKey, onSelect, guidedHref }: {
  sections: DosingSection[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  guidedHref?: string;
}) {
  return (
    <div className="mt-7 space-y-5">
      {sections.map(section => (
        <div key={section.title || 'doses'}>
          {section.title && <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--store-muted,#8a8175)] mb-2">{section.title}</div>}
          <div className="space-y-2">
            {section.options.map(({ key, plan, paths, available, limitedTo }) => {
              const active = key === selectedKey;
              return (
                <button
                  key={key}
                  onClick={() => onSelect(key)}
                  disabled={!available}
                  aria-pressed={active}
                  className={`w-full text-left rounded-xl px-4 py-3 min-h-[48px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    active
                      ? 'border-2 border-[var(--store-text,#1a1612)] bg-white'
                      : 'border border-[var(--store-border,#e8e2d6)] bg-white hover:border-[var(--store-muted,#8a8175)]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-[14px]">{plan.doseLabel}</div>
                    {section.byProgram && paths.length === 1 && (
                      <span className="flex-shrink-0 text-[10px] uppercase tracking-wider text-[var(--store-muted,#8a8175)] border border-[var(--store-border,#e8e2d6)] rounded-full px-2 py-0.5">{PATH_CHIPS[paths[0]]}</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--store-muted,#8a8175)] mt-0.5">
                    {planDisplayName(plan)}
                    {!available && ' · Not available in your state'}
                    {limitedTo && ` · Available in ${limitedTo.length > 4 ? `${limitedTo.length} states` : limitedTo.join(', ')}`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {guidedHref && (
        <Link to={guidedHref} className="block w-full text-center border border-[var(--store-border,#e8e2d6)] rounded-full py-3 text-sm font-medium hover:border-[var(--store-text,#1a1612)]">
          Not sure? Answer a few questions →
        </Link>
      )}
      <div className="text-[11px] text-[var(--store-muted,#8a8175)] leading-relaxed">
        Your clinician reviews your intake and confirms the final dose before anything ships.
      </div>
    </div>
  );
}

function ProseBlock({ heading, body }: { heading: string; body: string }) {
  return (
    <div>
      <h3 className="font-serif text-xl md:text-2xl font-medium tracking-tight mb-2">{heading}</h3>
      <div className="text-[var(--store-muted,#4a4238)] leading-[1.75] whitespace-pre-line">{body}</div>
    </div>
  );
}

function DoseRecommendation({ product, rationale, onAddTracked }: {
  product: DtpProductResponse; rationale: string;
  onAddTracked: (productId: string, source: string) => void;
}) {
  const { addItem, hasItem } = useDtpCartStore();
  const inCart = hasItem(product._id);

  const handleAdd = () => {
    if (inCart) {
      toast.success('Already in your cart');
      return;
    }
    const replaced = addItem({ productId: product._id, productName: product.displayName, price: product.price, family: doseFamilyKey(product) });
    onAddTracked(product._id, 'recommendation');
    toast.success(replaced ? `Switched to ${product.displayName} (replaced ${replaced.productName})` : `${product.displayName} added to cart`);
  };

  return (
    <div className="mt-7 pt-7 border-t border-[var(--store-border,#e8e2d6)]">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[var(--store-accent,#2d4a3a)] font-medium">
        <Check className="w-3 h-3" />Your match
      </div>
      <div className="font-serif text-xl md:text-[22px] font-medium mt-2 leading-tight">
        {product.drugName} {product.concentration}
        <div className="text-[var(--store-muted,#8a8175)] text-[15px] md:text-[16px] font-normal mt-0.5">{product.size} · {product.format}</div>
      </div>
      {rationale && <p className="text-[13px] text-[var(--store-muted,#4a4238)] mt-3 leading-relaxed">{rationale}</p>}
      <div className="mt-5 md:mt-6 flex items-baseline justify-between">
        <div className="font-serif text-[28px] md:text-[32px] font-medium">${product.price.toFixed(2)}</div>
        <div className="text-xs text-[var(--store-muted,#8a8175)]">est. {product.estimatedShipDays} day{product.estimatedShipDays === 1 ? '' : 's'}</div>
      </div>
      <button
        onClick={handleAdd}
        className="mt-5 w-full bg-[var(--store-text,#1a1612)] hover:bg-black text-white font-medium py-3.5 rounded-full flex items-center justify-center gap-2 min-h-[48px]"
      >
        {inCart ? <><Check className="w-4 h-4" />In cart</> : <><ShoppingCart className="w-4 h-4" />Add to cart</>}
      </button>
    </div>
  );
}

function MobileBuyBar({ product, label, onAddTracked }: {
  product: DtpProductResponse; label: string; onAddTracked: (productId: string, source: string) => void;
}) {
  const { addItem, hasItem } = useDtpCartStore();
  const inCart = hasItem(product._id);
  const handleAdd = () => {
    if (inCart) return;
    const replaced = addItem({ productId: product._id, productName: product.displayName, price: product.price, family: doseFamilyKey(product) });
    onAddTracked(product._id, 'sticky-bar');
    toast.success(replaced ? `Switched to ${product.displayName} (replaced ${replaced.productName})` : 'Added to cart');
  };
  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[var(--store-border,#e8e2d6)] px-4 py-3 z-30 flex items-center gap-3 shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.08)]" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-[var(--store-muted,#8a8175)] uppercase tracking-wider truncate">{label}</div>
        <div className="font-serif text-lg font-medium">${product.price.toFixed(2)}</div>
      </div>
      <button
        onClick={handleAdd}
        className="flex-shrink-0 bg-[var(--store-text,#1a1612)] text-white font-medium px-5 py-3 rounded-full flex items-center gap-2 min-h-[48px]"
      >
        {inCart ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
        {inCart ? 'In cart' : 'Add'}
      </button>
    </div>
  );
}

/** Leading number of a strength or size ("10mg/mL" → 10), for ordering doses. */
function leadingNumber(value?: string): number {
  const n = parseFloat(String(value || '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function sortByStrength(products: DtpProductResponse[]): DtpProductResponse[] {
  return [...products].sort((a, b) =>
    leadingNumber(a.concentration) - leadingNumber(b.concentration)
    || leadingNumber(a.size) - leadingNumber(b.size)
    || a.price - b.price);
}

/**
 * Every strength of a drug without program plans, as visible dose options in
 * the same style as program plans. Replaces the old cheapest-default with the
 * rest hidden behind "See all N options".
 */
function SkuDoseOptions({ products, selectedId, onSelect }: {
  products: DtpProductResponse[]; selectedId?: string; onSelect: (id: string) => void;
}) {
  return (
    <div className="mt-7 space-y-2" role="group" aria-label="Choose a strength">
      {sortByStrength(products).map(p => {
        const active = p._id === selectedId;
        return (
          <button
            key={p._id}
            onClick={() => onSelect(p._id)}
            aria-pressed={active}
            className={`w-full text-left rounded-xl px-4 py-3 min-h-[48px] transition-colors ${
              active
                ? 'border-2 border-[var(--store-text,#1a1612)] bg-white'
                : 'border border-[var(--store-border,#e8e2d6)] bg-white hover:border-[var(--store-muted,#8a8175)]'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="font-medium text-[14px]">{[p.concentration, p.size].filter(Boolean).join(' · ')}</div>
              <div className="font-serif text-[15px] font-medium flex-shrink-0">${p.price.toFixed(2)}</div>
            </div>
            <div className="text-xs text-[var(--store-muted,#8a8175)] mt-0.5">{p.format}</div>
          </button>
        );
      })}
    </div>
  );
}

export default DtpLearnFamily;
